import { describeError, useApi } from '@classgo/app-kit';
import { Card, EmptyState, Select, Tabs } from '@classgo/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { listQuery } from '../../academic/api';
import { useT } from '../../i18n';
import { recordQueries } from '../../records/api';
import { can } from '../../shell';
import { useTenant } from '../../tenant/tenant-provider';
import { EvaluationsTab } from './evaluations';
import { PeriodTable } from './period-table';
import { PublishControls } from './publish';
import { ScoresTab } from './score-grid';
import { decimalsOf, formatScore } from './support';

type Tab = 'period' | 'scores' | 'evaluations';

/**
 * Calificaciones (M0's read screen, extended by #115 over the API of #106): a group, a subject
 * and a period; the state of that cell (draft or published) with its publication for whoever
 * holds `grades:publish`; and, with `grades:write`, three tabs: the period grades with the
 * override per student, the score grid of one evaluation, and the evaluations themselves.
 * Without the key, the period grades alone, as before. The tabs stay mounted (hidden) so a
 * half-typed grid survives a look at the evaluations; their reads share one cache.
 */
export const GradesViewScreen = () => {
  const t = useT();
  const api = useApi();
  const { session, sede } = useTenant();
  const canWrite = can(session, 'grades:write');
  const canPublish = can(session, 'grades:publish');
  const groups = useQuery(listQuery(api, 'groups', { sedeId: sede.id }));
  const subjects = useQuery(listQuery(api, 'subjects'));
  const years = useQuery(listQuery(api, 'years'));
  const activeYear =
    years.data?.items.find((year) => year.status === 'active') ?? years.data?.items[0];
  const periods = useQuery({
    ...listQuery(api, 'periods', { schoolYearId: activeYear?.id ?? '' }),
    enabled: !!activeYear,
  });
  const [groupId, setGroupId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [tab, setTab] = useState<Tab>('period');
  useEffect(() => {
    if (!groupId && groups.data?.items[0]) setGroupId(groups.data.items[0].id);
    if (!subjectId && subjects.data?.items[0]) setSubjectId(subjects.data.items[0].id);
    if (!periodId && periods.data?.items[0]) setPeriodId(periods.data.items[0].id);
  }, [groupId, subjectId, periodId, groups.data, subjects.data, periods.data]);
  const grades = useQuery(recordQueries.grades(api, groupId, subjectId, periodId));
  const view = grades.data;
  const group = groups.data?.items.find((item) => item.id === groupId);
  const cell = { groupId, subjectId, periodId };
  const names = {
    group: group?.name ?? '',
    subject: subjects.data?.items.find((item) => item.id === subjectId)?.name ?? '',
    period: periods.data?.items.find((item) => item.id === periodId)?.name ?? '',
  };
  const decimals = decimalsOf(view?.scale);

  return (
    <Card title={t('grades.view.title')}>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <Select
          label={t('grades.view.filter.group')}
          value={groupId}
          onChange={(event) => setGroupId(event.target.value)}
          options={(groups.data?.items ?? []).map((item) => ({
            value: item.id,
            label: item.name,
          }))}
        />
        <Select
          label={t('grades.view.filter.subject')}
          value={subjectId}
          onChange={(event) => setSubjectId(event.target.value)}
          options={(subjects.data?.items ?? []).map((subject) => ({
            value: subject.id,
            label: subject.name,
          }))}
        />
        <Select
          label={t('grades.view.filter.period')}
          value={periodId}
          onChange={(event) => setPeriodId(event.target.value)}
          options={(periods.data?.items ?? []).map((period) => ({
            value: period.id,
            label: period.name,
          }))}
        />
      </div>
      {view?.scale ? (
        <p className="mb-3 text-xs text-gray-600">
          {t('grades.view.scale', {
            min: formatScore(view.scale.minScore, decimals),
            max: formatScore(view.scale.maxScore, decimals),
            pass: formatScore(view.scale.passMark, decimals),
          })}
        </p>
      ) : null}
      {grades.isError ? (
        <EmptyState title={t('entity.error.title')} description={describeError(t, grades.error)} />
      ) : !view ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : (
        <div className="space-y-4">
          <PublishControls
            key={`${groupId}|${subjectId}|${periodId}`}
            cell={cell}
            names={names}
            view={view}
            canPublish={canPublish}
          />
          {canWrite ? (
            <Tabs
              label={t('grades.view.title')}
              value={tab}
              onChange={(id) => setTab(id === 'scores' || id === 'evaluations' ? id : 'period')}
              items={[
                {
                  id: 'period',
                  label: t('gradebook.tab.period'),
                  content: <PeriodTable cell={cell} view={view} canWrite />,
                },
                {
                  id: 'scores',
                  label: t('gradebook.tab.scores'),
                  content: <ScoresTab cell={cell} view={view} />,
                },
                {
                  id: 'evaluations',
                  label: t('gradebook.tab.evaluations'),
                  content: (
                    <EvaluationsTab cell={cell} view={view} gradeId={group?.gradeId ?? ''} />
                  ),
                },
              ]}
            />
          ) : (
            <PeriodTable cell={cell} view={view} canWrite={false} />
          )}
        </div>
      )}
    </Card>
  );
};
